/**
 * ChatMessage - Single chat message bubble with copy/feedback actions
 */

import { motion } from 'framer-motion'
import { Brain, Copy, ThumbsUp, ThumbsDown } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { AIChatMessage } from './types'

interface ChatMessageProps {
  message: AIChatMessage
  onCopy?: (content: string) => void
  onFeedback?: (messageId: string, feedback: 'positive' | 'negative') => void
}

export function ChatMessage({ message, onCopy, onFeedback }: ChatMessageProps) {
  const isAssistant = message.role === 'assistant'

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn('flex gap-3', isAssistant ? 'flex-row' : 'flex-row-reverse')}
    >
      {/* Avatar */}
      <div
        className={cn(
          'flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center',
          isAssistant ? 'bg-gradient-to-br from-purple-500 to-blue-500' : 'bg-slate-200 dark:bg-slate-700'
        )}
      >
        {isAssistant ? (
          <Brain className="w-4 h-4 text-white" />
        ) : (
          <span className="text-sm font-medium text-slate-600 dark:text-slate-300">You</span>
        )}
      </div>

      {/* Content */}
      <div
        className={cn(
          'flex-1 max-w-[85%] rounded-2xl px-4 py-3',
          isAssistant
            ? 'bg-white dark:bg-slate-800 shadow-sm border border-slate-100 dark:border-slate-700'
            : 'bg-blue-500 text-white ml-auto'
        )}
      >
        <p className={cn('text-sm whitespace-pre-wrap', isAssistant ? 'text-slate-700 dark:text-slate-300' : 'text-white')}>
          {message.content}
        </p>

        {isAssistant && (
          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-100 dark:border-slate-700">
            <button
              onClick={() => onCopy?.(message.content)}
              className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600"
              title="Copy"
            >
              <Copy className="w-3 h-3" />
            </button>
            <button
              onClick={() => onFeedback?.(message.id, 'positive')}
              className="p-1 rounded hover:bg-green-100 dark:hover:bg-green-900/30 text-slate-400 hover:text-green-500"
              title="Helpful"
            >
              <ThumbsUp className="w-3 h-3" />
            </button>
            <button
              onClick={() => onFeedback?.(message.id, 'negative')}
              className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-slate-400 hover:text-red-500"
              title="Not helpful"
            >
              <ThumbsDown className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>
    </motion.div>
  )
}
